// Copyright (c) Ping. All rights reserved.
//
// Shareable invite links — two shapes:
//
//   1-1   `/u/<accountId>`              — the target's 16-byte account_id
//                                         (base64url). Recipient resolves the
//                                         profile + starts a 1-1 directly.
//   group `/g/<approverAccountId>/<groupId>` — an E2EE MLS group can't be joined
//                                         from a URL (a member must process the
//                                         joiner's KeyPackage → Welcome), so a
//                                         group link does NOT self-join. It names
//                                         a MEMBER who can admit (the sharer) +
//                                         the group id. Opening it starts a 1-1
//                                         with that member and sends a join
//                                         REQUEST; the member taps Approve, which
//                                         runs `addWebGroupMembers`. No backend.
//
// Resolved by the deep-link handler in `ChatRouteHost` via `readInviteFromLocation`.
// See docs/architecture/profiles-and-links.md §4.

const USER_PREFIX = '/u/';
const GROUP_PREFIX = '/g/';

/** Origin for outbound links. Falls back to the prod host off-DOM (SSR/tests). */
function appOrigin(): string {
  if (typeof location !== 'undefined' && location.origin.length > 0) return location.origin;
  return 'https://openmls.ping.live';
}

/** Build a shareable 1-1 invite link for `accountId` (base64url, 16-byte). */
export function buildUserInviteLink(accountId: string): string {
  return `${appOrigin()}${USER_PREFIX}${encodeURIComponent(accountId)}`;
}

/**
 * Build a shareable GROUP invite link. `approverAccountId` is a current group
 * member (the sharer) who will receive + approve the join request; `groupId` is
 * the MLS conversation id (hex) of the group.
 */
export function buildGroupInviteLink(approverAccountId: string, groupId: string): string {
  return `${appOrigin()}${GROUP_PREFIX}${encodeURIComponent(approverAccountId)}/${encodeURIComponent(groupId)}`;
}

/** A parsed invite link. */
export type ParsedInviteLink =
  | { readonly kind: 'user'; readonly accountId: string }
  | { readonly kind: 'group'; readonly approverAccountId: string; readonly groupId: string };

/** Parse a `/u/<id>` or `/g/<approver>/<groupId>` invite out of a URL or path. */
export function parseInviteLink(input: string): ParsedInviteLink | null {
  try {
    const url = input.startsWith('http') ? new URL(input) : new URL(input, 'http://x');
    const user = url.pathname.match(/^\/u\/([^/]+)\/?$/);
    if (user && user[1]) return { kind: 'user', accountId: decodeURIComponent(user[1]) };
    const group = url.pathname.match(/^\/g\/([^/]+)\/([^/]+)\/?$/);
    if (group && group[1] && group[2]) {
      return {
        kind: 'group',
        approverAccountId: decodeURIComponent(group[1]),
        groupId: decodeURIComponent(group[2]),
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** Read an invite (user or group) from the current location. */
export function readInviteFromLocation(): ParsedInviteLink | null {
  if (typeof location === 'undefined') return null;
  return parseInviteLink(location.pathname + location.search);
}

const INVITE_STASH_KEY = 'ping:pendingInvite';

/**
 * Capture an invite deep link from the current URL and stash it. MUST be called
 * at app startup (before React renders) — the router normalizes unknown paths to
 * `/chat` and `replaceState`s the URL in a mount effect, and on a cold navigation
 * that happens during bootstrap, long before the auth-gated `ChatRouteHost`
 * mounts to read `location`. Stashing in sessionStorage preserves the invite
 * across bootstrap + the auth flow (same tab), so the host can consume it
 * whenever it eventually mounts.
 */
export function captureInviteFromLocation(): void {
  if (typeof location === 'undefined') return;
  const invite = parseInviteLink(location.pathname + location.search);
  if (invite === null) return;
  try {
    sessionStorage.setItem(INVITE_STASH_KEY, JSON.stringify(invite));
  } catch {
    /* sessionStorage unavailable — `takePendingInvite` falls back to live URL */
  }
}

/**
 * Consume a previously-[captured](captureInviteFromLocation) invite (clearing it),
 * falling back to the live URL if nothing was stashed. Returns null when there's
 * no pending invite.
 */
export function takePendingInvite(): ParsedInviteLink | null {
  try {
    const raw = sessionStorage.getItem(INVITE_STASH_KEY);
    if (raw !== null && raw.length > 0) {
      sessionStorage.removeItem(INVITE_STASH_KEY);
      const parsed = JSON.parse(raw) as ParsedInviteLink;
      if (parsed.kind === 'user' || parsed.kind === 'group') return parsed;
    }
  } catch {
    /* fall through to the live-URL read */
  }
  return readInviteFromLocation();
}
